import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, Activity, Info } from 'lucide-react';

const AdminTelemetry = () => {
  // Detect current environment
  const currentEnvironment = useMemo(() => {
    const hostname = window.location.hostname;
    if (hostname === "interlaced.app") {
      return "production";
    }
    if (hostname.includes("preview")) return "preview";
    if (hostname.includes(".lovableproject.com") || hostname.includes("sandbox.lovable.dev")) {
      return "development";
    }
    if (hostname === "localhost" || hostname === "127.0.0.1") return "local";
    return "unknown";
  }, []);
  
  const serviceName = currentEnvironment === "production" ? "interlaced-app" : "interlaced-app-debug";

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Telemetria Datadog RUM</h1>
          <p className="text-muted-foreground mt-2">
            Monitore a instrumentação de Real User Monitoring
          </p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview">
            <Activity className="w-4 h-4 mr-2" />
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="config">
            <Info className="w-4 h-4 mr-2" />
            Configuração
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Session Replay
                </CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">100%</div>
                <p className="text-xs text-muted-foreground">
                  todas as sessões gravadas
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Privacy Level
                </CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Allow</div>
                <p className="text-xs text-muted-foreground">
                  captura completa de dados
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Status
                </CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Ativo</div>
                <p className="text-xs text-muted-foreground">
                  Datadog RUM operacional
                </p>
              </CardContent>
            </Card>
          </div>

          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              <strong>OpenTelemetry removido.</strong> A aplicação agora utiliza exclusivamente Datadog RUM para telemetria do browser.
              Todas as sessões são gravadas com captura completa de dados (defaultPrivacyLevel: "allow").
            </AlertDescription>
          </Alert>
        </TabsContent>

        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuração Atual</CardTitle>
              <CardDescription>
                Configurações do Datadog RUM
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="font-medium">Application ID:</div>
                <div className="font-mono text-xs">934b7197-abeb-43e3-be80-d0b71b4eb9ad</div>
                
                <div className="font-medium">Service Name:</div>
                <div className="font-mono text-xs flex items-center gap-2">
                  {serviceName}
                  <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                    currentEnvironment === "production" 
                      ? "bg-green-500/20 text-green-400" 
                      : "bg-blue-500/20 text-blue-400"
                  }`}>
                    {currentEnvironment}
                  </span>
                </div>
                
                <div className="font-medium">Session Sample Rate:</div>
                <div className="font-mono text-xs">100%</div>
                
                <div className="font-medium">Session Replay Rate:</div>
                <div className="font-mono text-xs">100%</div>
                
                <div className="font-medium">Privacy Level:</div>
                <div className="font-mono text-xs">
                  <Badge variant="default" className="bg-green-500">allow</Badge>
                </div>
                
                <div className="font-medium">Track Resources:</div>
                <div className="font-mono text-xs">✅ Enabled</div>
                
                <div className="font-medium">Track Long Tasks:</div>
                <div className="font-mono text-xs">✅ Enabled</div>
                
                <div className="font-medium">Track User Interactions:</div>
                <div className="font-mono text-xs">✅ Enabled</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Como Verificar no Datadog</CardTitle>
              <CardDescription>
                Passos para acessar os dados de RUM
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Acesse o Datadog RUM Explorer</li>
                <li>Filtre por <code className="bg-muted px-1 rounded">service:{serviceName}</code></li>
                <li>Visualize sessões, ações e erros</li>
                <li>Use Session Replay para ver gravações completas</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminTelemetry;
